
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useUser } from '@/contexts/UserContext';
import { Gasto } from "@shared/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { DataTableExport } from "@/components/DataTableExport";
import { Plus, Trash, Pencil, Upload, Loader2, FileText, X, AlertTriangle } from 'lucide-react';

const GASTO_COLUMNS: Array<{ id: keyof Gasto; name: string }> = [
  { id: 'proveedor', name: 'Proveedor' },
  { id: 'cif', name: 'CIF' },
  { id: 'numeroFactura', name: 'Nº Factura' },
  { id: 'fecha', name: 'Fecha' },
  { id: 'baseImponible', name: 'Base Imponible' },
  { id: 'iva', name: 'IVA' },
  { id: 'importeRetencion', name: 'Retención IRPF' },
  { id: 'total', name: 'Total Gasto' },
  { id: 'totalAPagar', name: 'Total a Pagar' },
  { id: 'descripcion', name: 'Descripción' },
];

export default function GastosPage() {
  const { fetchWithAuth } = useUser();
  const [gastos, setGastos] = useState<Gasto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{
    extractedData: any;
    existingGasto: any;
    fileBase64: string;
    fileName: string;
    mimeType: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const fetchGastos = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth("/api/gastos");
      if (!response.ok) throw new Error("No se pudieron obtener los gastos.");
      setGastos(await response.json());
    } catch (error) {
      toast.error("Error al cargar gastos", { description: error instanceof Error ? error.message : "Error desconocido" });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { fetchGastos() }, [fetchGastos]);

  const handleFileSelect = (files: FileList | null) => files && files.length > 0 && setSelectedFile(files[0]);
  const handleDragEvent = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    handleDragEvent(e);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleUpload = async (skipDuplicateCheck = false) => {
    if (!selectedFile && !duplicateInfo) return;
    setIsUploading(true);

    try {
        let fileBase64: string;
        let fileName: string;
        let mimeType: string;

        if (duplicateInfo && skipDuplicateCheck) {
            // Usar datos guardados del intento anterior
            fileBase64 = duplicateInfo.fileBase64;
            fileName = duplicateInfo.fileName;
            mimeType = duplicateInfo.mimeType;
        } else if (selectedFile) {
            console.log('Processing file:', selectedFile.name, 'Type:', selectedFile.type, 'Size:', selectedFile.size);
            fileName = selectedFile.name;
            mimeType = selectedFile.type;

            // Convert file to base64
            fileBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const base64 = (reader.result as string).split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(selectedFile);
            });
        } else {
            return;
        }

        console.log('File converted to base64, sending to API...');

        // Send base64 data directly to Cloud Function using fetchWithAuth
        const response = await fetchWithAuth("/api/gastos/process-ai", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                fileBase64,
                fileName,
                mimeType,
                skipDuplicateCheck
            }),
        });

        console.log('Response status:', response.status);

        // Manejar respuesta de duplicado detectado (409)
        if (response.status === 409) {
            const duplicateResponse = await response.json();
            if (duplicateResponse.isDuplicate) {
                setDuplicateInfo({
                    extractedData: duplicateResponse.extractedData,
                    existingGasto: duplicateResponse.existingGasto,
                    fileBase64,
                    fileName,
                    mimeType
                });
                setIsUploadDialogOpen(false);
                return; // No cerrar, mostrar diálogo de duplicado
            }
        }

        if (!response.ok) {
            let errorMessage = "Error al procesar la factura.";
            try {
                const responseText = await response.text();
                console.error('Error response:', responseText);

                try {
                    const errorData = JSON.parse(responseText);
                    errorMessage = errorData.message || errorMessage;
                } catch (parseError) {
                    errorMessage = responseText || errorMessage;
                }
            } catch (e) {
                console.error('Error reading response:', e);
            }
            throw new Error(errorMessage);
        }

        const newGasto = await response.json();
        toast.success(`Gasto de ${newGasto.proveedor || 'proveedor desconocido'} creado con éxito.`);
        fetchGastos();
        setIsUploadDialogOpen(false);
        setSelectedFile(null);
        setDuplicateInfo(null);
    } catch (error) {
        console.error('Upload error:', error);
        toast.error('Error al procesar', {
            description: error instanceof Error ? error.message : 'Error desconocido'
        });
    } finally {
        setIsUploading(false);
    }
  };

  const handleDuplicateConfirm = () => {
    // Proceder a guardar ignorando el duplicado
    handleUpload(true);
  };

  const handleDuplicateCancel = () => {
    setDuplicateInfo(null);
    setSelectedFile(null);
    toast.info('Procesamiento cancelado. La factura no se ha guardado.');
  };

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    const promise = fetchWithAuth(`/api/gastos/${deleteId}`, { method: "DELETE" }).then(res => {
      if (!res.ok) throw new Error('No se pudo eliminar el gasto');
    });

    toast.promise(promise, {
        loading: 'Eliminando gasto...',
        success: () => { fetchGastos(); setDeleteId(null); return 'Gasto eliminado'; },
        error: (err) => `Error: ${err.message}`,
    });
  };

  const formatCurrency = (value?: number | null) => value != null ? `${value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}` : '-';
  const formatDate = (date: string) => new Date(date).toLocaleDateString('es-ES');
  const totalGastos = useMemo(() => gastos.reduce((sum, gasto) => sum + (gasto.total || 0), 0), [gastos]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tighter">Gestión de Gastos</h1>
          <p className="text-muted-foreground mt-1">Registra y gestiona las facturas de tus proveedores.</p>
        </div>
        <div className="flex items-center gap-2">
          <DataTableExport data={gastos} columns={GASTO_COLUMNS} fileName="gastos" />
          <Button variant="outline" onClick={() => navigate('/gastos/nuevo')}><Plus className="mr-2 h-4 w-4" /> Añadir Manual</Button>
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild><Button><Upload className="mr-2 h-4 w-4" /> Escanear Gasto</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Escanear Factura de Gasto con IA</DialogTitle>
                <DialogDescription>Sube una imagen o PDF de tu factura y la IA extraerá los datos.</DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragOver ? 'border-primary bg-primary/10' : 'border-border'}`} onDragEnter={handleDragEvent} onDragOver={handleDragEvent} onDragLeave={handleDragEvent} onDrop={handleDrop}>
                  <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                  <p className="mt-4 text-muted-foreground">Arrastra y suelta o <Button variant="link" className="p-1" onClick={() => fileInputRef.current?.click()}>selecciona un archivo</Button></p>
                  <Input ref={fileInputRef} type="file" className="hidden" onChange={(e) => handleFileSelect(e.target.files)} accept="image/*,application/pdf" />
                </div>
                {selectedFile && <div className="flex items-center justify-between rounded-lg border p-3"><div className="flex items-center gap-2"><FileText className="h-5 w-5"/><span className="text-sm font-medium">{selectedFile.name}</span></div><Button variant="ghost" size="icon" onClick={() => setSelectedFile(null)}><X className="h-4 w-4"/></Button></div>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)} disabled={isUploading}>Cancelar</Button>
                <Button onClick={() => handleUpload()} disabled={!selectedFile || isUploading}>{isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>} Subir y Procesar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <Card>
        <CardHeader>
            <div className="flex justify-between items-start"><div><CardTitle>Mis Gastos</CardTitle><CardDescription>Lista de las facturas de gastos registradas.</CardDescription></div><div className="text-right"><p className="text-muted-foreground">Total Gastado</p><p className="text-2xl font-bold">{formatCurrency(totalGastos)}</p></div></div>
        </CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="h-12 w-12 mx-auto my-24 animate-spin text-primary" /> : gastos.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg"><p className="text-muted-foreground">No has registrado ningún gasto.</p><Button className="mt-4" onClick={() => setIsUploadDialogOpen(true)}><Upload className="mr-2 h-4 w-4"/> Escanear primer gasto</Button></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Nº Factura</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Base</TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead className="text-right">Ret. IRPF</TableHead>
                  <TableHead className="text-right">A Pagar</TableHead>
                  <TableHead className="text-right pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gastos.map((gasto) => (
                  <TableRow key={gasto.id} onDoubleClick={() => navigate(`/gastos/${gasto.id}/edit`)} className="hover:bg-muted/50 cursor-pointer">
                    <TableCell className="font-medium">{gasto.proveedor}</TableCell>
                    <TableCell className="text-muted-foreground">{gasto.numeroFactura || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(gasto.fecha)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(gasto.baseImponible)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(gasto.iva)}</TableCell>
                    <TableCell className="text-right font-mono text-orange-600">{gasto.importeRetencion ? `-${formatCurrency(gasto.importeRetencion)}` : '-'}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{formatCurrency(gasto.totalAPagar ?? gasto.total)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/gastos/${gasto.id}/edit`)}}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteId(gasto.id ?? null)}}><Trash className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer. El gasto será eliminado permanentemente.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDeleteConfirm}>Eliminar</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>

      {/* Diálogo de duplicado detectado */}
      <AlertDialog open={!!duplicateInfo} onOpenChange={() => !isUploading && setDuplicateInfo(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Posible factura duplicada
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                <p>Se ha detectado una factura con el mismo número y un importe similar ya registrada:</p>
                {duplicateInfo?.existingGasto && (
                  <div className="bg-muted/50 p-3 rounded-lg space-y-1 text-sm">
                    <p><span className="font-medium">Proveedor:</span> {duplicateInfo.existingGasto.proveedor}</p>
                    <p><span className="font-medium">Nº Factura:</span> {duplicateInfo.existingGasto.numeroFactura}</p>
                    <p><span className="font-medium">Fecha:</span> {new Date(duplicateInfo.existingGasto.fecha).toLocaleDateString('es-ES')}</p>
                    <p><span className="font-medium">Total:</span> {duplicateInfo.existingGasto.total?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                    <p className="text-xs text-muted-foreground">
                      Registrada el {new Date(duplicateInfo.existingGasto.fechaCreacion).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                )}
                <p className="text-amber-700 font-medium">¿Deseas guardar esta factura de todas formas?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDuplicateCancel} disabled={isUploading}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicateConfirm} disabled={isUploading} className="bg-amber-600 hover:bg-amber-700">
              {isUploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : 'Guardar de todas formas'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
